import { inject, Injectable } from '@angular/core';
import { FuelKpiRequest } from '../../_shared';
import { FuelKpiResponse, FuelKpiSummaryResponse } from '../../_shared/models/fuel-kpi-response.model';
import { Pagination, Response, SummaryRequest } from '../../../_shared/models';
import { Subject } from 'rxjs';
import { firstValueFrom, takeUntil } from 'rxjs';
import { environment } from '../../../../environments/environment';
import { HttpClient } from '@angular/common/http';
import { ConsumptionStatsResponse, FuelConsumptionDetailsResponse } from '../models/fuel-consumption-details.model';
import { AccountDateFilterRequest } from '../../../_shared/models/accountDateFilter.model';
import { FuelInsightsRequest } from '../models/fuel-insights-request.model';
import { FuelConsumptionRequest } from '../../_shared/models/fuel-consumption-request.model';

@Injectable({
  providedIn: 'root'
})
export class FuelConsumptionService {
  private http = inject(HttpClient)

  async getFuelEfficiencyKpiStats(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<FuelKpiResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/fuel-efficiency-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getTotalDistanceKpiStats(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<FuelKpiResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/total-distance-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getTotalTripsKpiStats(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<FuelKpiResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/total-trips-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getTotalFuelUsedKpiStats(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<FuelKpiResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/total-fuel-used-summary`, request).pipe(takeUntil(cancellation)));
  }

  async getFuelConsumptionDetails(request: FuelConsumptionRequest, cancellation: Subject<void>): Promise<Response<Pagination<FuelConsumptionDetailsResponse[]>>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/fuel-consumption-details`, request).pipe(takeUntil(cancellation)));
  }

  async getHighConsumptionDrivers(request: AccountDateFilterRequest, cancellation: Subject<void>): Promise<Response<ConsumptionStatsResponse[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/high-driver-consumption`, request).pipe(takeUntil(cancellation)));
  }

  async getHighConsumptionAssets(request: AccountDateFilterRequest, cancellation: Subject<void>): Promise<Response<ConsumptionStatsResponse[]>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/high-assets-consumption`, request).pipe(takeUntil(cancellation)));
  }

  async getHighConsumptionSummary(request: SummaryRequest, cancellation: Subject<void>): Promise<Response<FuelKpiResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelconsumption/high-consumption-summary`, request).pipe(takeUntil(cancellation)));
  }

  public getFuelConsumptionSummary(request: FuelKpiRequest, cancellation: Subject<void>): Promise<Response<FuelKpiSummaryResponse>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumption/fuel-consumption-summary`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalFuelConsumptionEconomyInsights(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/global-fuel-economy`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalCostPerUnit(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/global-fuel-cost-per-unit`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalConsumptionInsights(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/global-fuel-consumption`, request).pipe(takeUntil(cancellation)));
  }

  public getGlobalVolumeInsights(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/global-fuel-volume`, request).pipe(takeUntil(cancellation)));
  }

  public getMovingFuelEconomyInsights(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/fuel-economy`, request).pipe(takeUntil(cancellation)));
  }

  public getMovingFuelConsumptionInsights(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/fuel-consumption`, request).pipe(takeUntil(cancellation)));
  }

  public getMovingFuelCostPerUnit(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/fuel-cost-per-unit`, request).pipe(takeUntil(cancellation)));
  }

  public getMovingFuelVolumeInsights(request: FuelInsightsRequest, cancellation: Subject<void>): Promise<Response<any>> {
    return firstValueFrom(this.http.post(`${environment.apiEndpoint}/fuelConsumptionInsights/fuel-volume`, request).pipe(takeUntil(cancellation)));
  }
}